import { action, makeObservable, observable, runInAction } from 'mobx';
import { orderService } from '../../services';
import {
  ApiError,
  ClinicOrder,
  ClinicOrderQuery,
  CreateOrderRequest,
  PayRequest,
  ResendReceiptResult,
} from '../../types';

const DEFAULT_TAKE = 10;

export class OrderStore {
  items: ClinicOrder[] = [];
  total = 0;
  query: ClinicOrderQuery = { skip: 0, take: DEFAULT_TAKE };
  loadingList = false;
  listError: ApiError | null = null;

  detail: ClinicOrder | null = null;
  loadingDetail = false;
  detailError: ApiError | null = null;

  creating = false;
  paying = false;
  cancelling = false;
  confirming = false;
  reordering = false;

  constructor() {
    makeObservable(this, {
      items: observable,
      total: observable,
      query: observable,
      loadingList: observable,
      listError: observable,
      detail: observable,
      loadingDetail: observable,
      detailError: observable,
      creating: observable,
      paying: observable,
      cancelling: observable,
      confirming: observable,
      reordering: observable,
      setQuery: action,
      loadList: action,
      loadDetail: action,
      create: action,
      pay: action,
      simulatePayFailed: action,
      payGuest: action,
      cancel: action,
      cancelGuest: action,
      confirmReceived: action,
      reorder: action,
      clearDetail: action,
      reset: action,
    });
  }

  reset(): void {
    this.items = [];
    this.total = 0;
    this.query = { skip: 0, take: DEFAULT_TAKE };
    this.loadingList = false;
    this.listError = null;
    this.detail = null;
    this.loadingDetail = false;
    this.detailError = null;
  }

  setQuery(patch: Partial<ClinicOrderQuery>): void {
    const next: ClinicOrderQuery = { ...this.query, ...patch };
    if (!('skip' in patch)) next.skip = 0;
    this.query = next;
  }

  async loadList(): Promise<void> {
    this.loadingList = true;
    this.listError = null;
    try {
      const res = await orderService.list(this.query);
      runInAction(() => {
        this.items = res.items;
        this.total = res.total;
      });
    } catch (err) {
      runInAction(() => {
        this.listError = err as ApiError;
        this.items = [];
        this.total = 0;
      });
    } finally {
      runInAction(() => {
        this.loadingList = false;
      });
    }
  }

  async loadDetail(orderId: string): Promise<void> {
    this.loadingDetail = true;
    this.detailError = null;
    this.detail = null;
    try {
      const order = await orderService.detail(orderId);
      runInAction(() => {
        this.detail = order;
      });
    } catch (err) {
      runInAction(() => {
        this.detailError = err as ApiError;
      });
    } finally {
      runInAction(() => {
        this.loadingDetail = false;
      });
    }
  }

  clearDetail(): void {
    this.detail = null;
    this.detailError = null;
  }

  async create(req: CreateOrderRequest): Promise<ClinicOrder> {
    this.creating = true;
    try {
      const order = await orderService.create(req);
      runInAction(() => {
        this.detail = order;
      });
      return order;
    } finally {
      runInAction(() => {
        this.creating = false;
      });
    }
  }

  async pay(orderId: string, req: PayRequest): Promise<ClinicOrder> {
    this.paying = true;
    try {
      const order = await orderService.pay(orderId, req);
      runInAction(() => {
        this.detail = order;
      });
      return order;
    } finally {
      runInAction(() => {
        this.paying = false;
      });
    }
  }

  async simulatePayFailed(orderId: string): Promise<ClinicOrder> {
    this.paying = true;
    try {
      const order = await orderService.simulatePayFailed(orderId);
      runInAction(() => {
        this.detail = order;
      });
      return order;
    } finally {
      runInAction(() => {
        this.paying = false;
      });
    }
  }

  async payGuest(
    publicOrderNo: string,
    email: string,
    req: PayRequest,
  ): Promise<ClinicOrder> {
    this.paying = true;
    try {
      const order = await orderService.payGuest(publicOrderNo, email, req);
      runInAction(() => {
        this.detail = order;
      });
      return order;
    } finally {
      runInAction(() => {
        this.paying = false;
      });
    }
  }

  async cancel(orderId: string): Promise<ClinicOrder> {
    this.cancelling = true;
    try {
      const order = await orderService.cancel(orderId);
      runInAction(() => {
        this.detail = order;
      });
      return order;
    } finally {
      runInAction(() => {
        this.cancelling = false;
      });
    }
  }

  async cancelGuest(publicOrderNo: string, email: string): Promise<ClinicOrder> {
    this.cancelling = true;
    try {
      const order = await orderService.cancelGuest(publicOrderNo, email);
      runInAction(() => {
        this.detail = order;
      });
      return order;
    } finally {
      runInAction(() => {
        this.cancelling = false;
      });
    }
  }

  async confirmReceived(orderId: string): Promise<ClinicOrder> {
    this.confirming = true;
    try {
      const order = await orderService.confirmReceived(orderId);
      runInAction(() => {
        this.detail = order;
      });
      return order;
    } finally {
      runInAction(() => {
        this.confirming = false;
      });
    }
  }

  async lookup(orderId: string, email: string): Promise<ClinicOrder> {
    this.loadingDetail = true;
    this.detailError = null;
    this.detail = null;
    try {
      const order = await orderService.lookup(orderId, email);
      runInAction(() => {
        this.detail = order;
      });
      return order;
    } catch (err) {
      runInAction(() => {
        this.detailError = err as ApiError;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.loadingDetail = false;
      });
    }
  }

  async resendReceipt(orderId: string): Promise<ResendReceiptResult> {
    return orderService.resendReceipt(orderId);
  }

  async reorder(orderId: string): Promise<{ addedCount: number; skippedCount: number }> {
    this.reordering = true;
    try {
      const res = await orderService.reorder(orderId);
      return { addedCount: res.addedCount, skippedCount: res.skipped.length };
    } finally {
      runInAction(() => {
        this.reordering = false;
      });
    }
  }
}
